import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import type { CodeoSkill } from '../types';
import { check, hasStrokeDraw } from '../skillUsage';

export const mapFlowArcsSkill: CodeoSkill = {
    name: SKILL_NAMES.mapFlowArcs,
    kind: 'codeo',
    description:
        'Draw routes between places on a map along their great circles, one after another, each ' +
        'with a dot riding the drawing tip and its destination named as it lands — for shipping, ' +
        'travel, expansion or anything that moves from here to there.',
    checkLooksFor: 'an L5Geo map with a route drawn on with stroke-dash animation',
    documentUsesSkill: check((script) => /\bL5Geo\b/.test(script) && hasStrokeDraw(script)),
    reviewNote:
        'Routes drawn across the land and the borders, and names set on the map, are this ' +
        'technique working — not lines crossing shapes by mistake or text over a busy background.',
    instructions: `# Map Flow Arcs

Routes leave one place and arrive at others, drawn on one after another, each with a dot riding the drawing tip so the viewer follows the journey rather than watching a line appear. Each destination is named as its route lands. The arcs bend the way the real routes do because they follow the great circle between the two points.

Use it when something moves between places — shipments, flights, an expansion from a home market. For where one thing is, use \`${SKILL_NAMES.mapLocateZoom}\`; for how much is in each place, \`${SKILL_NAMES.mapChoroplethReveal}\`.

The map comes from \`L5Geo\`, which the host injects: every d3-geo projection and path generator, plus the world's countries and the US states. Never type a path's \`d\` — generate it.

## Markup

\`\`\`html
<div class="absolute inset-0">
  <svg id="map" class="absolute inset-0" width="1920" height="1080" viewBox="0 0 1920 1080">
    <path id="land" data-l5-allow-overlap fill="var(--l5-color-1, #94a3b8)" fill-opacity="0.28" />
    <g id="routes" fill="none" stroke="var(--l5-color-3, #f59e0b)" stroke-width="5" stroke-linecap="round"></g>
    <g id="stops" fill="var(--l5-color-3, #f59e0b)"></g>
    <g id="stop-labels" font-family="var(--l5-font-0), sans-serif" font-size="52" font-weight="700"
       fill="var(--l5-color-4, #f8fafc)"></g>
  </svg>
  <div class="absolute inset-0 flex flex-col items-start p-24">
    <h1 id="title" style="margin: 0; font-family: var(--l5-font-0), sans-serif; font-size: 80px; font-weight: 800; color: var(--l5-color-4, #f8fafc);">From London to four markets</h1>
  </div>
</div>
\`\`\`

The land is a faint fill: it is the ground the routes are drawn on, and the routes and names carry the contrast.

## Timeline code

\`\`\`js
const tl = gsap.timeline({ paused: true });
const NS = 'http://www.w3.org/2000/svg';
const make = (tag, parent, attributes) => {
  const element = document.createElementNS(NS, tag);
  Object.entries(attributes).map(([key, value]) => element.setAttribute(key, value));
  return parent.appendChild(element);
};

// [longitude, latitude] — longitude first, the opposite of how coordinates are usually spoken.
const origin = [-0.13, 51.51];
const destinations = [
  { label: 'New York', at: [-74.01, 40.71], side: 'left' },
  { label: 'Lagos', at: [3.38, 6.52], side: 'right' },
  { label: 'Dubai', at: [55.27, 25.2], side: 'below' },
  { label: 'Singapore', at: [103.82, 1.35], side: 'right' },
];

// Centred on the origin's meridian, so no route from it can reach the edge of the map and split.
const world = L5Geo.countries().features.filter((country) => country.properties.name !== 'Antarctica');
const projection = L5Geo.geoNaturalEarth1()
  .rotate([-origin[0], 0])
  .fitExtent([[96, 220], [1824, 1000]], { type: 'FeatureCollection', features: world });
const path = L5Geo.geoPath(projection);
document.querySelector('#land').setAttribute('d', path({ type: 'FeatureCollection', features: world }));

const routes = document.querySelector('#routes');
const stops = document.querySelector('#stops');
const labels = document.querySelector('#stop-labels');

// Each name goes on the side of its dot that no route crosses, picked by looking at where the
// other routes run: one heading past a stop passes straight over a name set beside it.
const LABEL_OFFSETS = {
  left: { dx: -28, dy: 0, anchor: 'end' },
  right: { dx: 28, dy: 0, anchor: 'start' },
  below: { dx: 0, dy: 56, anchor: 'middle' },
};
const pin = ({ label, at, side }) => {
  const [x, y] = projection(at);
  const dot = make('circle', stops, { cx: x, cy: y, r: 12 });
  const { dx, dy, anchor } = LABEL_OFFSETS[side];
  const text = make('text', labels, { x: x + dx, y: y + dy, 'text-anchor': anchor, 'dominant-baseline': 'central' });
  text.append(label);
  return { dot, text };
};

const [homeX, homeY] = projection(origin);
const home = make('circle', stops, { cx: homeX, cy: homeY, r: 16 });
gsap.set(['#title', '#land', home], { opacity: 0 });
tl.to('#title', { opacity: 1, duration: 0.6, ease: 'power2.out' }, 0.2);
tl.to('#land', { opacity: 1, duration: 0.8, ease: 'power2.out' }, 0.4);
tl.to(home, { opacity: 1, duration: 0.4, ease: 'power2.out' }, 0.9);

destinations.map((destination, i) => {
  // A LineString between two points is drawn along the great circle, so the route bends the way
  // the real one does instead of cutting a straight chord across the projection.
  const route = make('path', routes, { d: path({ type: 'LineString', coordinates: [origin, destination.at] }) });
  const length = Math.ceil(route.getTotalLength()) + 2;
  const head = make('circle', stops, { cx: homeX, cy: homeY, r: 9 });
  const stop = pin(destination);
  const start = 1.3 + i * 0.45;

  gsap.set(route, { strokeDasharray: length, strokeDashoffset: length + 1 });
  gsap.set([head, stop.dot, stop.text], { opacity: 0 });
  gsap.set(stop.dot, { scale: 0, transformOrigin: 'center center' });

  tl.to(route, { strokeDashoffset: 0, duration: 1.1, ease: 'power2.inOut' }, start);
  tl.set(head, { opacity: 1 }, start);
  tl.to(head, { motionPath: { path: route, align: route, alignOrigin: [0.5, 0.5] }, duration: 1.1, ease: 'power2.inOut' }, start);
  tl.to(head, { opacity: 0, duration: 0.2 }, start + 1.1);
  tl.to(stop.dot, { opacity: 1, scale: 1, duration: 0.35, ease: 'back.out(2.2)' }, start + 1.0);
  return tl.to(stop.text, { opacity: 1, duration: 0.4, ease: 'power2.out' }, start + 1.1);
});

window.__timelines = { main: tl };
\`\`\`

The head rides the tip because it shares the draw's start, duration and ease exactly. Give it a different ease and it runs ahead of the line or drags behind it.

## Adapt

- **Within the US**: \`L5Geo.geoAlbersUsa()\` fitted to \`L5Geo.usNation()\`, drawing \`L5Geo.usStates()\` or \`L5Geo.usStateBorders()\` as the ground. Great circles between US cities are nearly straight, so for a visible arc build a quadratic curve from the projected end points: \`M x1 y1 Q mx (min(y1, y2) - |x2 - x1| * 0.15) x2 y2\`, where \`mx\` is the midpoint's x.
- **Weighted routes**: when the plan carries a figure per route, set each stroke width from it — 3px for the smallest up to 10px for the largest — and draw the heaviest first.
- **A network rather than a hub**: draw each route from its own origin, but keep it to five or six. A dozen arcs drawn at once is a tangle, whatever the data says.
- **Return trips**: draw the outbound route, hold, then draw a second path whose \`LineString\` runs the other way. Reversing a dash-offset tween does not read as travel back.

## Pitfalls

- **Coordinates are \`[longitude, latitude]\`.** Swapped, a route to New York lands in Antarctica. Use the city's real coordinates, not a position on the map.
- **Centre the projection on the origin** with \`.rotate([-origin[0], 0])\`, as above. A route that crosses the edge of the projection is cut in two, one piece at each side of the map, and both pieces draw at once — Toronto to Tokyo leaves Canada heading west and reappears out of the right-hand edge. A great circle from the centre meridian never reaches the edge, so centring on the hub rules this out for every route. For a network with no single hub, centre on the middle of the routes' longitudes and check that none of their paths contains a second \`M\`.
- **Keep the dash padding.** Park the dash at \`Math.ceil(getTotalLength()) + 2\` and the offset one unit further, as \`${SKILL_NAMES.svgDrawOn}\` explains; an exact-length dash leaves a round-capped dot sitting at each origin before its route draws.
- **Start the head at the origin.** A circle with no position sits at the svg's corner until its tween begins, hanging off the frame.
- **Place each name by hand, away from the routes.** A name beside its dot is usually right, but a later route can pass straight through it. Look at where the other routes run and set the name on the clear side. The origin is the hardest: every route leaves from it, so its name goes on the side no destination lies toward, or in the title as above.
- **Never mark a name \`data-l5-allow-overlap\` to quiet a route crossing it.** The warning is the only thing that sees a line struck through a name; move the name instead. The mark belongs on the land, which names are meant to sit on.
- **Names in a colour other than the routes'.** A name in the route colour merges with any route that passes near it.
- **Keep the land faint.** At full strength it competes with the routes, and white names over a light land colour lose their contrast.`,
};
